import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_elfaiotbbx/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserWithout, validateToken, refreshAccessToken, revokeRefreshToken} from './_requests'
import {WithChildren} from '../../../../_elfaiotbbx/helpers'

/**
 * Erisim token'i suresi dolmadan ne kadar once yenilenir.
 *
 * Tam dolma aninda yenilemek yaris demek: cagri yolda iken token suresi doluyor ve
 * o sirada baslayan baska istekler 401 aliyor. Bes dakika, yavas bir agda bile birkac
 * deneme hakki birakiyor.
 */
const REFRESH_MARGIN_MS = 5 * 60 * 1000

/**
 * Yenileme basarisiz olursa ne kadar sonra tekrar denenir.
 *
 * Basarisizlik cogu zaman kalici degil: gecici ag kesintisi, backend yeniden dagitimi,
 * ya da baska bir sekmenin ayni anda yenileme yapmasi. Bu yuzden basarisizlik
 * **cikis sebebi degil**; bkz. runRefresh.
 */
const REFRESH_RETRY_MS = 30 * 1000

/** Hesaplanan gecikme negatif cikarsa (token zaten dolmus) kullanilan taban. */
const MIN_REFRESH_DELAY_MS = 1000

/**
 * Base64url -> metin. JWT govdesi base64url kodlu: '+' yerine '-', '/' yerine '_' ve
 * dolgu ('=') atilmis olabiliyor. `atob` duz base64 bekledigi icin geri cevirmek gerekiyor.
 */
function base64UrlDecode(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  return atob(padded)
}

/**
 * Erisim token'inin bitis zamani (milisaniye), okunamazsa `null`.
 *
 * ## Imza dogrulanmiyor -- neden sorun degil
 *
 * Burada yapilan tek sey **zamanlama**: yenilemeyi ne zaman tetikleyecegimizi bulmak.
 * Token'in gecerli olup olmadigina sunucu karar veriyor (gateway her istegi
 * auth-service'e dogrulatiyor). Istemcide uydurulmus bir `exp` yalnizca yenilemenin
 * yanlis anda tetiklenmesine yol acar, yetki kazandirmaz.
 *
 * ## Neden sunucudan "kac saniye sonra dolacak" istemiyoruz
 *
 * `exp` standart bir JWT alani ve token'in kendi icinde. Sunucudaki omur ayari
 * degisirse (EXPIRES_ACCESS_TOKEN_MINUTE) istemci hicbir degisiklik olmadan uyum
 * sagliyor; ayrica yanitta ayri bir alan tasimaya gerek kalmiyor.
 */
function accessTokenExpiry(token?: string): number | null {
  if (!token) {
    return null
  }
  // Depodaki deger "Bearer eyJ..." bicimli: onek, arayuzun onu oldugu gibi
  // Authorization basligina koymasi icin sunucu tarafindan ekleniyor.
  const jwt = token.startsWith('Bearer ') ? token.slice('Bearer '.length) : token
  const parts = jwt.split('.')
  if (parts.length !== 3) {
    return null
  }
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]))
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null
  } catch (error) {
    console.warn('Erisim token\'inin bitis zamani okunamadi', error)
    return null
  }
}

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  /**
   * Yenileme basarisiz oldugunda tekrar denemeyi tetiklemek icin.
   *
   * Zamanlayici `auth.token` degisimine bakiyor; basarisiz yenilemede token degismedigi
   * icin tek basina yeni bir zamanlayici kurulmaz. Bu sayac onu saglıyor.
   */
  const [refreshAttempt, setRefreshAttempt] = useState(0)
  const lastRefreshFailed = useRef(false)

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    const stored = authHelper.getAuth()
    if (stored?.refreshToken) {
      // Sonucu BEKLEMIYORUZ. Cikis sunucuya ulasilamasa da gerceklesmeli; aksi halde
      // kullanici agi kopukken cikis yapamazdi. revokeRefreshToken kendi icinde hata
      // yakaliyor, yani yakalanmamis bir promise reddi olusmuyor.
      void revokeRefreshToken(stored.refreshToken)
    }
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  /**
   * Erisim token'ini yeniler.
   *
   * ## Depodan okuyor, state'ten degil
   *
   * `auth` state'i yerine `authHelper.getAuth()`: bu fonksiyon bir zamanlayicidan
   * cagriliyor ve zamanlayicinin kapanisindaki state eski olabilir. Daha onemlisi
   * localStorage sekmeler arasinda **paylasilan** depo -- baska bir sekme token'i
   * yenilediyse guncel deger orada.
   *
   * ## Basarisizlikta CIKIS YAPMIYOR
   *
   * Yenilemenin basarisiz olmasinin kalici olmayan sebepleri var: gecici ag kesintisi,
   * backend yeniden dagitimi, ya da baska bir sekmenin ayni token'la ayni anda yenileme
   * yapmasi (sunucu donguyu tek kullanimlik tutuyor, ikinci cagri reddediliyor).
   * Bunlarin hicbiri kullaniciyi oturumdan atmayi hak etmiyor -- ustelik localStorage
   * paylasildigi icin bir sekmenin cikisi butun sekmeleri atardi.
   *
   * Onun yerine 30 saniye sonra tekrar deneniyor; o deneme depoyu yeniden okudugu icin
   * diger sekmenin yazdigi yeni token'i buluyor. Gercekten gecersiz bir oturumda ise
   * son soz bilesenlerdeki 401 -> logout() kontrollerinde kaliyor.
   */
  const runRefresh = async () => {
    const stored = authHelper.getAuth()
    if (!stored?.token || !stored.refreshToken) {
      // Yenileme token'i olmayan oturum: bu surumden onceki bir giristen kalmis.
      // Eski davranis gecerli -- token dolunca bilesenler 401 gorup cikis yapiyor.
      lastRefreshFailed.current = false
      return
    }
    try {
      const next = await refreshAccessToken(stored.refreshToken)
      lastRefreshFailed.current = false
      saveAuth({
        token: next.token,
        // Sunucu her kullanimda yeni bir yenileme token'i veriyor (dongu). Yanitta
        // yoksa eskisini koruyoruz -- eski bir backend surumune karsi calisma hali.
        refreshToken: next.refreshToken ?? stored.refreshToken,
        username: stored.username,
      })
      // Bilesenler token'i `currentUser.token` uzerinden okuyor; orayi guncellemezsek
      // yeni token yalnizca depoda kalir ve ekrandaki bilesenler eskisini kullanmaya
      // devam eder.
      setCurrentUser((previous) => (previous ? {...previous, token: next.token} : previous))
    } catch (error) {
      console.warn('Erisim token\'i yenilenemedi, tekrar denenecek', error)
      lastRefreshFailed.current = true
      setRefreshAttempt((attempt) => attempt + 1)
    }
  }

  /**
   * Suresi dolmadan once yenilemeyi zamanlar.
   *
   * ## Neden onden yenileme, 401'de yenileme degil
   *
   * Arayuzde merkezi bir istek katmani yok: ~20 bilesen kendi `fetch` cagrisini ve kendi
   * `createHeader()` fonksiyonunu tasiyor. 401 gorulup token yenilenip istegin yeniden
   * denmesi icin o yirmi yerin hepsine dokunmak gerekirdi. Onden yenilemede ise 401
   * hic olusmuyor ve o yirmi kontrol **oldugu gibi kaliyor** -- gercek yetki hatalari
   * icin emniyet agi olarak.
   */
  useEffect(() => {
    if (!auth?.token || !auth?.refreshToken) {
      return
    }
    const expiry = accessTokenExpiry(auth.token)
    if (expiry === null) {
      // Beklenmeyen bicimde bir token. Zamanlanmis yenileme kurulmuyor; 401 emniyet
      // agi devrede kaliyor.
      return
    }
    const delay = lastRefreshFailed.current
      ? REFRESH_RETRY_MS
      : Math.max(expiry - Date.now() - REFRESH_MARGIN_MS, MIN_REFRESH_DELAY_MS)
    const timer = window.setTimeout(() => {
      void runRefresh()
    }, delay)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line
  }, [auth?.token, auth?.refreshToken, refreshAttempt])

  /**
   * Diger sekmelerdeki oturum degisikliklerini yansitir.
   *
   * localStorage sekmeler arasinda paylasiliyor ama React state'i paylasilmiyor. Bu
   * dinleyici olmadan: bir sekme token'i yeniledikten sonra diger sekme eski token'la
   * kaliyor ve suresi dolunca 401 alip **butun sekmeleri** cikis yaptiriyor; ya da bir
   * sekmede cikis yapildiginda digeri depo bos oldugu halde oturumda gorunmeye devam
   * ediyor.
   *
   * `storage` olayi yalnizca **diger** sekmelerde tetikleniyor, yazan sekmede degil --
   * yani burada dongu olusmuyor.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      // event.key, localStorage.clear() cagrildiginda null oluyor; o durumu da isliyoruz.
      if (event.key && event.key !== authHelper.AUTH_LOCAL_STORAGE_KEY) {
        return
      }
      const stored = authHelper.getAuth()
      if (!stored?.token) {
        // Baska sekme cikis yapti. logout() CAGIRMIYORUZ: o, sunucuya ikinci bir iptal
        // istegi gonderir ve depoyu tekrar temizlemeye calisirdi. Yalnizca durumu
        // yansitiyoruz.
        setAuth(undefined)
        setCurrentUser(undefined)
        return
      }
      setAuth(stored)
      setCurrentUser((previous) => (previous ? {...previous, token: stored.token} : previous))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {saveAuth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    // Tek sefer. Oncesinde bu bayrak istekten SONRA ve bir async fonksiyonun donus
    // degerinde ayarlaniyordu (`return () => (didRequest.current = true)`), yani
    // hicbir zaman bir seyi engellemiyordu.
    if (didRequest.current) {
      return
    }
    didRequest.current = true

    const loadUser = async (accessToken: string, username: string) => {
      const user = await getUserWithout(accessToken, username)
      user.token = accessToken
      setCurrentUser(user)
    }

    /**
     * Acilista oturumu geri yukler.
     *
     * Oncesinde: token gecersizse dogrudan `logout()`. Kullanici sekmeyi bir gece kapali
     * biraktiysa (token omru 30 dakikaydi) sabah kesinlikle yeniden giris yapmak
     * zorundaydi. Artik once yenileme token'i deneniyor -- 30 gun gecerli oldugu icin
     * oturum sabaha kaliyor.
     */
    const restoreSession = async () => {
      const stored = authHelper.getAuth()
      if (!stored?.token || !stored.username) {
        logout()
        setShowSplashScreen(false)
        return
      }
      try {
        await validateToken(stored.token)
        await loadUser(stored.token, stored.username)
      } catch (accessTokenError) {
        if (!stored.refreshToken) {
          // Bu surumden onceki bir giristen kalan oturum: yenilenecek bir sey yok.
          console.error(accessTokenError)
          logout()
          return
        }
        try {
          const next = await refreshAccessToken(stored.refreshToken)
          saveAuth({
            token: next.token,
            refreshToken: next.refreshToken ?? stored.refreshToken,
            username: stored.username,
          })
          await loadUser(next.token, stored.username)
        } catch (refreshError) {
          // Burada cikis DOGRU: erisim token'i da yenileme token'i da calismiyor.
          // Zamanlayicidaki toleransli davranis (bkz. runRefresh) buraya uymuyor,
          // cunku acilista gosterecek bir oturum yok.
          console.error(refreshError)
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }
    }

    void restoreSession()
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
